import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { OutlinedPrimaryButton } from "../ui/Button";
import mobileAppMockup from "../../assets/images/qrc_mob_app.png";
import customerPortalMockup from "../../assets/images/qrc_customer_portal.png";
import whatsappChatMockup from "../../assets/images/whatsup_chat.png";
import mobileAppMockupSm from "../../assets/images/m_qrc_mob_app.png";
import customerPortalMockupSm from "../../assets/images/m_qrc_customer_portal.png";
import whatsappChatMockupSm from "../../assets/images/m_whatsup_chat.png";
import { ROUTES } from "../../routes/routerConfig";

const CARD_BORDER = "1px solid rgba(254, 193, 13, 0.35)";
// Sized so the longest description (Customer Portal) still fits the two lines the design shows —
// at 165 it spilled onto a third line, which stretched every card in the row taller.
const DEVICE_PANEL_WIDTH = 150;
// The cream disc behind each device, straight from Figma: a 248px circle hung off the card's
// top-left corner (top -29, left -66) and clipped by the card's overflow.
const BLOB = {
  size: 248,
  top: -29,
  left: -66,
  gradient:
    "linear-gradient(150.37deg, rgba(255, 255, 255, 0.14) 7.71%, rgba(254, 193, 13, 0.14) 81.87%)",
} as const;
const RAISE_REQUEST_URL = ROUTES.Login.index;
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.customermobapp.avanse&pcampaignid=web_share";
const APP_STORE_URL = "https://apps.apple.com/in/app/avanse-financial-services/id6752022757";
// Pre-filled "Hi Team, Need help" so the chatbot opens on an opening message.
const WHATSAPP_URL =
  "https://api.whatsapp.com/send/?phone=919819080080&text=Hi%20Team,%20Need%20help&type=phone_number&app_absent=0";

// The design has a single "Explore App" button, so pick the store that matches the device: iOS
// (including iPadOS, which reports itself as MacIntel with touch points) goes to the App Store,
// everything else — Android and desktop — to Play.
const mobileAppStoreUrl = (): string => {
  if (typeof navigator === "undefined") return PLAY_STORE_URL;
  const isIos =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return isIos ? APP_STORE_URL : PLAY_STORE_URL;
};

interface Channel {
  title: string;
  description: string;
  cta: string;
  // Destination for the CTA. Empty means no link yet — the click is a no-op rather than opening
  // a dead tab.
  href: string;
  // Device mockup at its exported size. The two phones are taller than the card on purpose:
  // the card clips them at the bottom edge, as in the design.
  image: string;
  // The phone-sized export (130×150) used below the sm breakpoint, where the wider desktop
  // mockup would be clipped down to a sliver by the narrower card.
  mobileImage: string;
  imageWidth: number;
  imageHeight: number;
}

const CHANNELS: Channel[] = [
  {
    title: "Mobile App",
    description: "Access your loan, pay EMIs, track status and more—anytime, anywhere.",
    cta: "Explore App",
    href: mobileAppStoreUrl(),
    image: mobileAppMockup,
    mobileImage: mobileAppMockupSm,
    imageWidth: 130,
    imageHeight: 153,
  },
  {
    title: "Customer Portal",
    description: "Secure self-service for account management, documents, and real-time updates.",
    cta: "Open Portal",
    href: RAISE_REQUEST_URL,
    image: customerPortalMockup,
    mobileImage: customerPortalMockupSm,
    imageWidth: 148,
    imageHeight: 91,
  },
  {
    title: "WhatsApp Chatbot",
    description: "Instant support for quick queries, EMI info, request tracking and more.",
    cta: "Chat Now",
    href: WHATSAPP_URL,
    image: whatsappChatMockup,
    mobileImage: whatsappChatMockupSm,
    imageWidth: 104,
    imageHeight: 135,
  },
];

// "Manage your loan on your terms" — the digital-channel cards at the bottom of the
// pre-login Help Center and Contact Us hub.
const DigitalChannels: React.FC = () => (
  <Box sx={{ mt: 4 }}>
    <Typography sx={{ fontWeight: 700, fontSize: { xs: "16px", sm: "18px" }, color: "#1a1a1a" }}>
      Manage your loan on your terms
    </Typography>
    <Typography sx={{ color: "#6b7280", fontSize: "13px", mt: 0.5, mb: 2.5 }}>
      Choose your preferred digital channel for 24x7 access and support.
    </Typography>

    <Grid container spacing={2}>
      {CHANNELS.map((channel) => (
        <Grid item xs={12} sm={6} md={4} key={channel.title}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "stretch",
              height: "100%",
              minHeight: 140,
              bgcolor: "#fff",
              border: CARD_BORDER,
              borderRadius: "10px",
              // Clips both the cream disc and the taller phone mockups at the card edges.
              overflow: "hidden",
            }}
          >
            {/* Cream disc — positioned against the card, not the device panel, per the Figma spec */}
         

            <Box
              sx={{
                position: "relative",
                width: DEVICE_PANEL_WIDTH,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* 600px is MUI's sm breakpoint — the same width at which the Grid drops from
                  three cards to one, so the art switches exactly when the card does. <picture>
                  means only the matching export is fetched, never both. */}
              <picture>
                <source media="(min-width:600px)" srcSet={channel.image} />
                <Box component="img" src={channel.mobileImage} alt="" aria-hidden />
              </picture>
            </Box>

            {/* Design has ~32px above the title and ~16px below the button; the text block, not
                the mockup, sets the card height at roughly 160px. */}
            <Box sx={{ position: "relative", flex: 1, minWidth: 0, pr: 2, pt: 4, pb: 2,ml: 6 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1a1a1a" }}>
                {channel.title}
              </Typography>
              <Typography sx={{ color: "#6b7280", fontSize: "12px", mt: 1, lineHeight: 1.6 }}>
                {channel.description}
              </Typography>
              {/* Rendered in its normal teal-outlined state to match the design. The href is
                  still missing for all three channels, so the click is a guarded no-op until the
                  app-store / portal / WhatsApp links are supplied. */}
              <OutlinedPrimaryButton
                variant="outlined"
                height="32px"
                borderRadius="6px"
                backgroundColor="#fff"
                endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  if (channel.href) window.open(channel.href, "_blank", "noopener,noreferrer");
                }}
                sx={{ mt: 1.5, px: 1.5 }}
              >
                {channel.cta}
              </OutlinedPrimaryButton>
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default DigitalChannels;
